import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api'
import { useToast } from '../components/Toast'
import { useAsyncData } from '../hooks/useAsyncData'
import { useColumnPrefs } from '../hooks/useColumnPrefs'
import RefreshButton from '../components/RefreshButton'
import HScroll from '../components/HScroll'
import Spinner, { PageSpinner } from '../components/Spinner'
import ColumnsMenu from '../components/ColumnsMenu'
import FiltersMenu from '../components/FiltersMenu'
import ConfirmDialog from '../components/ConfirmDialog'
import {
  ACCOUNT_TYPE_COLORS, ACCOUNT_TYPE_LABELS, ACCOUNT_TYPES, ENVIRONMENTS, ENV_COLORS,
  FEDERATIONS, PARTNER_ROLES,
} from '../constants'
import Instant from '../components/Instant'

// The API rejects federations on a PARTNER account and partner fields on a CUSTOMER
// one, so the form only ever submits the half that applies.
const EMPTY_FORM = {
  email: '',
  environment: '',
  account_type: 'CUSTOMER',
  password: '',
  federations: ['emporia'],
  partner_company: '',
  partner_role: '',
  partner_gid: '',
  has_public_charger: false,
}

// Email and the Reserve action always show.
const TOGGLEABLE_COLUMNS = [
  { key: 'environment', label: 'Environment' },
  { key: 'accountType', label: 'Type' },
  { key: 'federations', label: 'Federations' },
  { key: 'partner', label: 'Partner' },
  { key: 'publicCharger', label: 'Public charger' },
  { key: 'created', label: 'Created' },
  { key: 'status', label: 'Status' },
]

// Filters offered in the Filters dropdown. Each is a three-way switch; '' means
// "don't filter on this".
const ANY_YES_NO = [
  { value: '', label: 'Any' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]

const FILTER_OPTIONS = [
  {
    key: 'has_public_charger',
    label: 'Public charger',
    hint: 'Partner logins that have a public charger on them',
    choices: ANY_YES_NO,
  },
]

const DEFAULT_COLUMNS = {
  environment: true,
  accountType: true,
  federations: true,
  partner: false,
  publicCharger: true,
  created: true,
  status: true,
}

export default function Accounts() {
  const [reserving, setReserving] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [addOpen, setAddOpen] = useState(false)
  const [addForm, setAddForm] = useState(EMPTY_FORM)
  const [adding, setAdding] = useState(false)
  const addDialogRef = useRef(null)
  const [removeTarget, setRemoveTarget] = useState(null)
  const [envFilter, setEnvFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  // Query values per filter: '' (don't filter), 'true', or 'false'.
  const [filterFlags, setFilterFlags] = useState({ has_public_charger: '' })
  const { columns, toggle: toggleColumn, reset: resetColumns, isDefault: isDefaultColumns } =
    useColumnPrefs('accounts.visibleColumns', DEFAULT_COLUMNS)
  const { show } = useToast()

  const { data, setData, loading, hasLoaded, reload } = useAsyncData(async () => {
    const params = {
      ...(envFilter ? { environment: envFilter } : {}),
      ...(typeFilter ? { account_type: typeFilter } : {}),
      ...(filterFlags.has_public_charger
        ? { has_public_charger: filterFlags.has_public_charger }
        : {}),
    }
    const [accounts, reservations] = await Promise.all([
      api.getAccounts(params),
      api.getReservations({ includeAll: true }),
    ])
    return { accounts, reservations }
  }, [envFilter, typeFilter, filterFlags], { onError: err => show(err.message, 'error') })

  const accounts = data?.accounts ?? []
  const reservations = data?.reservations ?? []

  // Native <dialog> gives us the focus trap, initial focus and Escape handling.
  useEffect(() => {
    const el = addDialogRef.current
    if (!el) return
    if (addOpen && !el.open) el.showModal()
    else if (!addOpen && el.open) el.close()
  }, [addOpen])

  function closeAddModal() {
    setAddOpen(false)
    setAddForm(EMPTY_FORM)
  }

  function setFormField(key, value) {
    setAddForm(f => {
      const next = { ...f, [key]: value }
      // Switching type clears the half that no longer applies, so a stale value
      // can't be submitted and rejected.
      if (key === 'account_type') {
        if (value === 'PARTNER') {
          next.federations = []
        } else {
          next.federations = f.federations.length ? f.federations : ['emporia']
          next.partner_company = ''
          next.partner_role = ''
          next.partner_gid = ''
          next.has_public_charger = false
        }
      }
      return next
    })
  }

  function toggleFormFederation(key) {
    setAddForm(f => ({
      ...f,
      federations: f.federations.includes(key)
        ? f.federations.filter(x => x !== key)
        : [...f.federations, key],
    }))
  }

  async function addAccount(e) {
    e.preventDefault()
    const isPartnerForm = addForm.account_type === 'PARTNER'
    if (!isPartnerForm && addForm.federations.length === 0) {
      show('Select at least one federation for a customer account', 'error')
      return
    }
    const body = {
      email: addForm.email.trim(),
      environment: addForm.environment,
      account_type: addForm.account_type,
      password: addForm.password,
      ...(isPartnerForm
        ? {
            has_public_charger: addForm.has_public_charger,
            ...(addForm.partner_company ? { partner_company: addForm.partner_company } : {}),
            ...(addForm.partner_role ? { partner_role: addForm.partner_role } : {}),
            ...(addForm.partner_gid ? { partner_gid: Number(addForm.partner_gid) } : {}),
          }
        : {
            federation_emporia: addForm.federations.includes('emporia'),
            federation_google: addForm.federations.includes('google'),
            federation_apple: addForm.federations.includes('apple'),
          }),
    }
    setAdding(true)
    try {
      const created = await api.createAccount(body)
      show(`Added "${created.email}"`, 'success')
      closeAddModal()
      reload()
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setAdding(false)
    }
  }

  async function reserve(account) {
    setReserving(account.id)
    try {
      await api.createReservation(account.id, 'ACCOUNT')
      show(`Reserved "${account.email}"`, 'success')
      // Refetch ALL reservations — fetching only ours would hide everyone
      // else's "Reserved" status until the next full reload.
      const res = await api.getReservations({ includeAll: true })
      setData(d => ({ ...d, reservations: res }))
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setReserving(null)
    }
  }

  async function removeAccount(account) {
    setRemoving(account.id)
    try {
      await api.deleteAccount(account.id)
      show(`Removed "${account.email}"`, 'success')
      setData(d => ({ ...d, accounts: d.accounts.filter(x => x.id !== account.id) }))
      setRemoveTarget(null)
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setRemoving(null)
    }
  }

  const activeReservedIds = new Set(
    reservations
      .filter(r => r.reservable_type === 'ACCOUNT')
      .map(r => r.reservable_id)
  )

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold mr-2">Accounts</h1>
        <button className="btn btn-sm btn-primary" onClick={() => setAddOpen(true)}>+ Add account</button>

        <select
          className="select select-sm select-bordered"
          aria-label="Filter by environment"
          value={envFilter}
          onChange={e => setEnvFilter(e.target.value)}
        >
          {['', ...ENVIRONMENTS].map(v => (
            <option key={v} value={v}>{v || 'All environments'}</option>
          ))}
        </select>

        <select
          className="select select-sm select-bordered"
          aria-label="Filter by account type"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
        >
          <option value="">All types</option>
          {ACCOUNT_TYPES.map(v => (
            <option key={v} value={v}>{ACCOUNT_TYPE_LABELS[v]}</option>
          ))}
        </select>

        <FiltersMenu
          options={FILTER_OPTIONS}
          values={filterFlags}
          onChange={(key, value) => setFilterFlags(f => ({ ...f, [key]: value }))}
        />

        <ColumnsMenu
          options={TOGGLEABLE_COLUMNS}
          columns={columns}
          onToggle={toggleColumn}
          onReset={resetColumns}
          isDefault={isDefaultColumns}
        />

        <RefreshButton onClick={reload} />
      </div>

      {!hasLoaded ? (
        <PageSpinner />
      ) : accounts.length === 0 ? (
        <div className="text-center py-20 opacity-40">No accounts found</div>
      ) : (
        // Keep the current rows on screen while a filter change refetches — dimmed
        // rather than replaced by a spinner, so the table doesn't collapse and the
        // page doesn't jump. Rows stay clickable; actions are keyed by id, so acting
        // on a row that is about to be filtered out is still correct.
        <div
          aria-busy={loading}
          className={`transition-opacity ${loading ? 'opacity-50' : ''}`}
        >
          <HScroll className="rounded-box border border-base-300">
            <table className="table">
              <thead>
                <tr>
                  <th>Email</th>
                  {columns.environment && <th>Environment</th>}
                  {columns.accountType && <th>Type</th>}
                  {columns.federations && <th>Federations</th>}
                  {columns.partner && <th>Partner</th>}
                  {columns.publicCharger && <th>Public charger</th>}
                  {columns.created && <th>Created</th>}
                  {columns.status && <th>Status</th>}
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(account => {
                  const isReserved = activeReservedIds.has(account.id)
                  const isPartner = account.account_type === 'PARTNER'
                  const feds = [
                    account.federation_emporia && 'emporia',
                    account.federation_google && 'google',
                    account.federation_apple && 'apple',
                  ].filter(Boolean)
                  return (
                    <tr key={account.id} className="hover">
                      <td className="font-medium">
                        <Link to={`/accounts/${account.id}`} className="link link-hover">
                          {account.email}
                        </Link>
                      </td>
                      {columns.environment && (
                        <td>
                          <span className={`badge badge-sm ${ENV_COLORS[account.environment] ?? 'badge-neutral'}`}>
                            {account.environment}
                          </span>
                        </td>
                      )}
                      {columns.accountType && (
                        <td>
                          <span
                            className={`badge badge-sm ${ACCOUNT_TYPE_COLORS[account.account_type] ?? 'badge-ghost'}`}
                          >
                            {ACCOUNT_TYPE_LABELS[account.account_type] ?? account.account_type}
                          </span>
                        </td>
                      )}
                      {columns.federations && (
                        <td>
                          {/* Partner accounts sign in through their own pool, so
                              federations don't apply rather than being empty. */}
                          {isPartner ? (
                            <span className="opacity-30">—</span>
                          ) : (
                            <div className="flex gap-1 flex-wrap">
                              {feds.length > 0
                                ? feds.map(f => (
                                    <span key={f} className="badge badge-sm badge-outline">{f}</span>
                                  ))
                                : <span className="opacity-40">none</span>
                              }
                            </div>
                          )}
                        </td>
                      )}
                      {columns.partner && (
                        <td className="opacity-70 text-sm">
                          {isPartner ? (
                            <div className="flex flex-col leading-tight">
                              <span>{account.partner_company ?? <span className="opacity-40">unnamed</span>}</span>
                              {account.partner_role && (
                                <span className="text-xs opacity-60">
                                  {account.partner_role}
                                  {account.partner_gid != null && ` · gid ${account.partner_gid}`}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="opacity-30">—</span>
                          )}
                        </td>
                      )}
                      {columns.publicCharger && (
                        <td>
                          {/* The charger itself isn't inventory-tracked — it comes with
                              this partner login, so reserving the account gets it. */}
                          {account.has_public_charger ? (
                            <span
                              className="badge badge-sm badge-warning"
                              title="This partner login has a public (Invite2Charge) charger on it"
                            >
                              yes
                            </span>
                          ) : isPartner ? (
                            <span className="opacity-40">no</span>
                          ) : (
                            <span className="opacity-30">—</span>
                          )}
                        </td>
                      )}
                      {columns.created && (
                        <td className="opacity-70">
                          <Instant iso={account.created_at} style="date" />
                        </td>
                      )}
                      {columns.status && (
                        <td>
                          {isReserved && (
                            <span className="badge badge-sm badge-error">Reserved</span>
                          )}
                        </td>
                      )}
                      <td>
                        <div className="flex gap-2">
                          <button
                            className="btn btn-sm btn-primary"
                            disabled={reserving === account.id || isReserved}
                            aria-busy={reserving === account.id}
                            onClick={() => reserve(account)}
                          >
                            {reserving === account.id ? <Spinner size="xs" /> : 'Reserve'}
                          </button>
                          {/* Disabled buttons don't show their title, so the tooltip
                              lives on a wrapper and the reason is repeated sr-only. */}
                          <span
                            className={isReserved ? 'cursor-not-allowed' : undefined}
                            title={isReserved ? 'Reserved or bundled accounts cannot be removed' : undefined}
                          >
                            <button
                              className={`btn btn-sm btn-ghost whitespace-nowrap ${isReserved ? 'btn-disabled' : 'text-error'}`}
                              disabled={removing === account.id || isReserved}
                              aria-busy={removing === account.id}
                              title={isReserved ? undefined : 'Remove account'}
                              onClick={() => setRemoveTarget(account)}
                            >
                              {removing === account.id ? <Spinner size="xs" /> : 'Remove'}
                              {isReserved && <span className="sr-only"> (reserved or bundled accounts cannot be removed)</span>}
                            </button>
                          </span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </HScroll>
        </div>
      )}

      <dialog
        ref={addDialogRef}
        className="modal"
        aria-labelledby="add-account-title"
        onCancel={e => { if (adding) e.preventDefault() }}
        onClose={() => { if (addOpen) closeAddModal() }}
      >
        <div className="modal-box w-full max-w-lg">
          <h3 id="add-account-title" className="font-bold text-lg mb-4">Add account</h3>
          <form onSubmit={addAccount} className="flex flex-col gap-3">
            <label className="form-control">
              <div className="label"><span className="label-text">Email <span className="text-error">*</span></span></div>
              <input
                type="email"
                className="input input-bordered input-sm"
                placeholder="qa+something@emporiaenergy.com"
                value={addForm.email}
                onChange={e => setFormField('email', e.target.value)}
                required
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="form-control">
                <div className="label"><span className="label-text">Environment <span className="text-error">*</span></span></div>
                <select
                  className="select select-bordered select-sm"
                  value={addForm.environment}
                  onChange={e => setFormField('environment', e.target.value)}
                  required
                >
                  <option value="">Select…</option>
                  {ENVIRONMENTS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>

              <label className="form-control">
                <div className="label"><span className="label-text">Type <span className="text-error">*</span></span></div>
                <select
                  className="select select-bordered select-sm"
                  value={addForm.account_type}
                  onChange={e => setFormField('account_type', e.target.value)}
                  required
                >
                  {ACCOUNT_TYPES.map(v => (
                    <option key={v} value={v}>{ACCOUNT_TYPE_LABELS[v]}</option>
                  ))}
                </select>
              </label>
            </div>

            <label className="form-control">
              <div className="label"><span className="label-text">Password <span className="text-error">*</span></span></div>
              <input
                className="input input-bordered input-sm font-mono"
                placeholder="QA-only, readable via the credentials endpoint"
                value={addForm.password}
                onChange={e => setFormField('password', e.target.value)}
                required
              />
            </label>

            {addForm.account_type === 'PARTNER' ? (
              <>
                <div className="divider my-0 text-xs">Partner details</div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={addForm.has_public_charger}
                    onChange={e => setFormField('has_public_charger', e.target.checked)}
                  />
                  <span className="text-sm">Has a public charger</span>
                </label>
                <p className="text-xs text-base-content/40 -mt-1 ml-7">
                  The charger isn&apos;t tracked as a device — it comes with this login, so
                  reserving the account gets it.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <label className="form-control">
                    <div className="label"><span className="label-text">Company</span></div>
                    <input
                      className="input input-bordered input-sm"
                      placeholder="optional"
                      value={addForm.partner_company}
                      onChange={e => setFormField('partner_company', e.target.value)}
                    />
                  </label>

                  <label className="form-control">
                    <div className="label"><span className="label-text">Role</span></div>
                    <select
                      className="select select-bordered select-sm"
                      value={addForm.partner_role}
                      onChange={e => setFormField('partner_role', e.target.value)}
                    >
                      <option value="">optional</option>
                      {PARTNER_ROLES.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </label>
                </div>

                <label className="form-control">
                  <div className="label"><span className="label-text">Partner GID</span></div>
                  <input
                    type="number"
                    className="input input-bordered input-sm font-mono"
                    placeholder="optional — the cloud partners.partner_gid"
                    value={addForm.partner_gid}
                    onChange={e => setFormField('partner_gid', e.target.value)}
                  />
                </label>
              </>
            ) : (
              <div>
                <div className="label"><span className="label-text">Federations <span className="text-error">*</span></span></div>
                <div className="flex flex-wrap gap-4">
                  {FEDERATIONS.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={addForm.federations.includes(key)}
                        onChange={() => toggleFormFederation(key)}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="modal-action mt-2">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={closeAddModal}
                disabled={adding}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={adding} aria-busy={adding}>
                {adding ? <Spinner size="xs" /> : 'Add account'}
              </button>
            </div>
          </form>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button disabled={adding} aria-label="Close">close</button>
        </form>
      </dialog>

      <ConfirmDialog
        open={!!removeTarget}
        title="Remove account"
        message={
          `Remove ${ACCOUNT_TYPE_LABELS[removeTarget?.account_type] ?? removeTarget?.account_type} ` +
          `account "${removeTarget?.email}" (${removeTarget?.environment}) from inventory? ` +
          `This only removes the QA inventory record — the account itself stays in Emporia Cloud. ` +
          `This cannot be undone.`
        }
        confirmLabel="Remove"
        busy={!!removeTarget && removing === removeTarget.id}
        onConfirm={() => removeAccount(removeTarget)}
        onClose={() => setRemoveTarget(null)}
      />
    </div>
  )
}
